import { useEffect, useState } from 'react';
import { FaPlus, FaEdit, FaTrash, FaEye, FaEyeSlash } from 'react-icons/fa';
import { fetchLitters } from '../../lib/api';
import { saveLitter, deleteLitter, setVisible } from '../../lib/adminApi';
import { ImageField, MultiImageField } from '../components/ImageField';

const slugify = (s) =>
  (s || '').toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const empty = {
  id: '', letter: '', date: '', descriptionPl: '', descriptionEn: '',
  presentationImage: null, gallery: [],
};

const LittersManagementPage = () => {
  const [litters, setLitters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setLitters(await fetchLitters({ includeHidden: true })); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleVisible = async (l) => {
    try { await setVisible('litters', l.id, l.visible === false); await load(); }
    catch (err) { alert(err.message); }
  };

  const openNew = () => { setEditing({ ...empty }); setIsNew(true); };
  const openEdit = (l) => {
    setEditing({
      id: l.id, letter: l.letter || '', date: l.date || '',
      descriptionPl: l.description?.pl || '', descriptionEn: l.description?.en || '',
      presentationImage: l.presentationImage || null, gallery: l.gallery || [],
    });
    setIsNew(false);
  };

  const handleSave = async () => {
    const litter = { ...editing };
    if (!litter.letter.trim()) { alert('Podaj literę miotu'); return; }
    if (isNew && !litter.id.trim()) litter.id = 'miot-' + slugify(litter.letter);
    if (!litter.id.trim()) { alert('Podaj identyfikator (slug)'); return; }
    setSaving(true);
    try { await saveLitter(litter); setEditing(null); await load(); }
    catch (err) { alert('Błąd zapisu: ' + err.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (l) => {
    if (!window.confirm(`Usunąć miot „${l.letter}"?`)) return;
    try { await deleteLitter(l.id); await load(); }
    catch (err) { alert('Błąd usuwania: ' + err.message); }
  };

  const set = (k, v) => setEditing((p) => ({ ...p, [k]: v }));

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <p className="text-gray-600">Łącznie miotów: <span className="font-semibold">{litters.length}</span></p>
        <button onClick={openNew} className="inline-flex items-center gap-2 bg-brand-accent text-white px-4 py-2 rounded-lg hover:opacity-90">
          <FaPlus /> Dodaj miot
        </button>
      </div>

      {loading ? <p className="text-gray-500">Ładowanie...</p> : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 divide-y">
          {litters.map((l) => {
            const hidden = l.visible === false;
            return (
            <div key={l.id} className={`flex items-center gap-4 p-4 ${hidden ? 'opacity-50' : ''}`}>
              <img src={l.presentationImage} alt={l.letter} className="w-14 h-14 rounded object-cover bg-gray-100" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-800">Miot {l.letter}
                  {hidden && <span className="ml-2 text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full align-middle">ukryty</span>}
                </p>
                <p className="text-sm text-gray-500">{l.date} · {l.gallery?.length || 0} zdjęć</p>
              </div>
              <button onClick={() => toggleVisible(l)} className={`p-2 ${hidden ? 'text-gray-400 hover:text-green-600' : 'text-gray-500 hover:text-brand-accent'}`} title={hidden ? 'Pokaż na stronie' : 'Ukryj na stronie'}>
                {hidden ? <FaEyeSlash /> : <FaEye />}
              </button>
              <button onClick={() => openEdit(l)} className="p-2 text-gray-500 hover:text-brand-accent"><FaEdit /></button>
              <button onClick={() => handleDelete(l)} className="p-2 text-gray-500 hover:text-red-600"><FaTrash /></button>
            </div>
            );
          })}
          {litters.length === 0 && <p className="p-6 text-gray-500">Brak miotów.</p>}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center p-4 z-50 overflow-auto">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl my-8">
            <div className="p-5 border-b flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-800">{isNew ? 'Nowy miot' : `Edycja: Miot ${editing.letter}`}</h3>
              <button onClick={() => setEditing(null)} className="text-gray-400 hover:text-gray-700 text-xl">×</button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Litera *</label>
                  <input value={editing.letter} onChange={(e) => set('letter', e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2" placeholder="C" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Identyfikator</label>
                  <input value={editing.id} onChange={(e) => set('id', e.target.value)} disabled={!isNew} className="w-full border border-gray-300 rounded-md px-3 py-2 disabled:bg-gray-100" placeholder="miot-c" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data</label>
                  <input value={editing.date} onChange={(e) => set('date', e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2" placeholder="4 kwietnia 2024" />
                </div>
              </div>

              <ImageField label="Zdjęcie prezentacyjne" value={editing.presentationImage} onChange={(v) => set('presentationImage', v)} folder="litters" />
              <MultiImageField label="Galeria miotu" value={editing.gallery} onChange={(v) => set('gallery', v)} folder="litters" />

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Opis (PL)</label>
                  <textarea value={editing.descriptionPl} onChange={(e) => set('descriptionPl', e.target.value)} rows={6} className="w-full border border-gray-300 rounded-md px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Opis (EN)</label>
                  <textarea value={editing.descriptionEn} onChange={(e) => set('descriptionEn', e.target.value)} rows={6} className="w-full border border-gray-300 rounded-md px-3 py-2" />
                </div>
              </div>
            </div>
            <div className="p-5 border-t flex justify-end gap-3">
              <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50">Anuluj</button>
              <button onClick={handleSave} disabled={saving} className="px-4 py-2 rounded-md bg-brand-accent text-white hover:opacity-90 disabled:opacity-50">
                {saving ? 'Zapisywanie...' : 'Zapisz'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LittersManagementPage;
